import { useEffect, useState } from 'react';
import { Grid, Typography, Button, CircularProgress, useTheme, darken } from '@mui/material';
import StateManager from '../../StateManager';
import { ArrowDropDownRounded } from '@mui/icons-material';
import { SearchMenu } from '../../Components';
import { isArray, isEmpty } from 'lodash';
import axios from 'axios';

function GetOptionPath(option, allOptions) {
  if (!option) return [];

  if (!option.parentId || !isArray(allOptions)) return [option.text];

  const parent = allOptions.find((x) => x.id === option.parentId);

  return [...GetOptionPath(parent, allOptions), option.text];
}

export default function DropboxField({ external, field, initial, onChange, editable }) {
  // for global fields
  const [loadingOptions, setLoadingOptions] = useState(false);
  // the options to be shown: for global fields - fetched options, otherwise just item options
  const [fieldOptions, setFieldOptions] = useState([]);
  // the selected options
  const [selectedValue, setSelectedValue] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    setSelectedValue(initial ? initial : '');
  }, [initial]);

  // need to run only the first time
  useEffect(() => {
    // the component shouldn't be created without 'field' prop
    if (!field) return;

    if (!editable) {
      setFieldOptions(isArray(field.options) ? field.options : []);
      return;
    }

    // for global fields - fetch the latest versions
    if (field.globalFieldId) {
      setLoadingOptions(true);
      const invitationId = localStorage.getItem('invitationId');
      axios
        .get(external ? '/external/fields/getGlobalFieldData' : '/fields/groups/getGlobalFieldData', {
          params: { globalFieldId: field.globalFieldId, invitationId },
        })
        .then(({ data }) => {
          setFieldOptions(isArray(data?.options) ? data.options : []);
        })
        .catch((err) => {
          StateManager.setAxiosErrorAlert(err);
        })
        .finally(() => {
          setLoadingOptions(false);
        });
    } else {
      // not a global field - just use options from the field
      setFieldOptions(isArray(field.options) ? field.options : []);
    }
  }, [editable]); // eslint-disable-line

  if (!field || !['dropbox', 'weightedList', 'status'].includes(field.fieldType)) return null;

  function handleOptionClick(optionId) {
    if (!editable) return;
    setSelectedValue(optionId);
    onChange(optionId);
  }

  if (editable) {
    if (loadingOptions) {
      // waiting for the global fields options
      return <CircularProgress size={24} />;
    }

    const optionSelected = selectedValue ? fieldOptions.find((o) => o.id === selectedValue) : null;
    const optionColor = optionSelected?.color ? optionSelected.color : '';
    const optionHoverColor = optionSelected?.color ? darken(optionSelected.color, 0.2) : '';
    const tree = GetOptionPath(optionSelected, fieldOptions);

    return (
      <Grid container>
        <Button
          sx={{
            minWidth: 220,
            borderColor: optionColor,
            textTransform: 'none',
            color: optionColor ? (theme) => theme.palette.getContrastText(optionColor) : '',
            background: optionSelected ? optionColor : '',
            '&:hover': {
              background: optionSelected ? optionHoverColor : '',
              borderColor: optionHoverColor,
            },
          }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          endIcon={<ArrowDropDownRounded />}
          variant={'outlined'}
        >
          <Typography noWrap>{!isEmpty(tree) ? tree.join(' / ') : 'Select value'}</Typography>
        </Button>

        <SearchMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          items={fieldOptions}
          onResult={(optionId) => handleOptionClick(optionId)}
          minWidth={220}
          withColor={fieldOptions.some((x) => x.color)}
        />
      </Grid>
    );
  } else {
    // selectedOptions have priority
    const options = isArray(field.filledValue?.selectedOptions) ? field.filledValue.selectedOptions : fieldOptions;

    const optionSelected = selectedValue ? options.find((o) => o.id === selectedValue) : null;
    const optionColor = optionSelected?.color ? optionSelected.color : '';
    const tree = GetOptionPath(optionSelected, options);

    if (field.fieldType === 'status' && optionColor) {
      return (
        <Grid container>
          <Button
            sx={{
              minWidth: 220,
              textTransform: 'none',
              color: (theme) => theme.palette.getContrastText(optionColor),
              background: optionColor,
              '&.Mui-disabled': {
                color: (theme) => theme.palette.getContrastText(optionColor),
              },
            }}
            disabled
          >
            {tree.join(' / ')}
          </Button>
        </Grid>
      );
    }

    return (
      <Grid container>
        <Typography sx={{ fontWeight: 500, color: optionColor }}>{tree.join(' / ')}</Typography>
      </Grid>
    );
  }
}
